import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { CreateAnimalForm } from 'src/@types/validations/Animal'
import { CoverageProcessParams } from 'src/domain/coverage-process'
import { PregnancyDiagnosisDto } from 'src/dto/pregnancy-diagnosis.dto'
import { CoverageProcessDto } from 'src/dto/coverage-process.dto'
import { toast } from 'react-toastify'
import { toastSyncSuccess } from 'src/utils/syncToast'
import { formParams } from '..'
import { calculateBirthForecast } from 'src/utils'
import { SaveDiagnosisCompleteParams } from 'src/services/api/pregnancy-diagnosis.service'
import { useAuth } from 'src/contexts/auth'
import { useNavigate } from 'react-router'
// import ROUTES from 'src/constants/routes'

interface UseBreedingListDetailsFormParams {
  pregnancyDiagnosis?: PregnancyDiagnosisDto | null
  coverageProcess?: CoverageProcessDto | null
  handleSave: (payload?: CoverageProcessParams) => Promise<void>
  handleSaveDiagnosis: (_payload?: any) => Promise<void>
  /**
   * Novo método consolidado que orquestra toda a operação
   * @param params - Parâmetros incluindo diagnóstico, processo e flag de atualização
   */
  handleSaveDiagnosisComplete?: (params: SaveDiagnosisCompleteParams) => Promise<any>
}

export const useBreedingListDetailsForm = ({
  pregnancyDiagnosis,
  coverageProcess,
  handleSave,
  handleSaveDiagnosis,
  handleSaveDiagnosisComplete
}: UseBreedingListDetailsFormParams) => {
  const [savedBirthAnimals, setSavedBirthAnimals] = useState<CreateAnimalForm[]>([])
  const [useDateInterval, setUseDateInterval] = useState(false)
  const [hasJustSaved, setHasJustSaved] = useState(false)

  const { watch, setValue, reset, setError, clearErrors } = useFormContext<formParams>()
  const navigate = useNavigate()
  const { username } = useAuth()

  useEffect(() => {
    if (!pregnancyDiagnosis && !coverageProcess) return

    const hasIntervalDates =
      !!coverageProcess?.dataInicio &&
      !!coverageProcess?.dataFim &&
      coverageProcess.dataInicio !== coverageProcess.dataFim

    const computedDataInicial = hasIntervalDates
      ? new Date(
          (new Date(coverageProcess!.dataInicio!).getTime() + new Date(coverageProcess!.dataFim!).getTime()) / 2
        ).toISOString()
      : pregnancyDiagnosis?.dataInicial || ''

    reset({
      pregnancyDiagnosis: {
        dataDiagnostico: pregnancyDiagnosis?.dataDiagnostico || new Date().toISOString(),
        diagnostico: pregnancyDiagnosis?.diagnostico! || 'prenha',
        idAnimal: pregnancyDiagnosis?.idAnimal || '',
        idDiagGest: pregnancyDiagnosis?.idDiagGest || '',
        touro: pregnancyDiagnosis?.touro || '',
        tipoReproducao: pregnancyDiagnosis?.tipoReproducao! || 'monta',
        dataInicial: computedDataInicial
      },
      coverageProcess: {
        dataFim: coverageProcess?.dataFim || '',
        dataCio: coverageProcess?.dataCio || '',
        dataInicio: coverageProcess?.dataInicio || '',
        idLote: coverageProcess?.idLote || '',
        idTouro: coverageProcess?.idTouro || '',
        idVaca: coverageProcess?.idVaca || '',
        inseminador: coverageProcess?.inseminador || '',
        tipoMonta: coverageProcess?.tipoMonta! || 'monta'
      }
    })

    setUseDateInterval(hasIntervalDates)
    setSavedBirthAnimals(pregnancyDiagnosis?.listCrias || [])
    // Reset hasJustSaved quando mudar o diagnóstico
    setHasJustSaved(false)
  }, [coverageProcess, pregnancyDiagnosis, reset])

  const hasBirthAnimalsFromDiagnosis = (pregnancyDiagnosis?.listCrias?.length || 0) > 0
  const diagnosisStatus = watch('pregnancyDiagnosis.diagnostico') || 'prenha'
  const diagnosisDate = watch('pregnancyDiagnosis.dataDiagnostico') || ''

  const handleToggleDateInterval = () => {
    const nextUseDateInterval = !useDateInterval

    setUseDateInterval(nextUseDateInterval)

    if (nextUseDateInterval) {
      // Quando ativar intervalo, definir data início como ontem e data fim como hoje
      const beginDate = new Date()
      beginDate.setDate(beginDate.getDate() - 1)
      const endDate = new Date()

      setValue('coverageProcess.dataInicio', beginDate.toISOString(), { shouldDirty: true })
      setValue('coverageProcess.dataFim', endDate.toISOString(), { shouldDirty: true })
      return
    }

    // Quando desativar intervalo, preservar a data inicial já salva ou usar data de hoje
    const savedDate = pregnancyDiagnosis?.dataInicial || pregnancyDiagnosis?.dataCobertura
    const dateToUse = savedDate || new Date().toISOString()
    setValue('pregnancyDiagnosis.dataInicial', dateToUse, { shouldDirty: !savedDate })
  }

  const handleSaveMultipleBirths = (births: CreateAnimalForm[]) => {
    setSavedBirthAnimals(births)
  }

  const handleRemoveBirth = (index: number) => {
    setSavedBirthAnimals((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSaveDiagnosisSection = async () => {
    if (!pregnancyDiagnosis) return

    // Calcula a previsão de parto baseado no tempo de gestação
    const previsaoParto = calculateBirthForecast(
      diagnosisDate,
      (pregnancyDiagnosis as any)?.tempoGestacao || null,
      pregnancyDiagnosis?.dataCobertura || pregnancyDiagnosis?.dataInicial
    )

    const { acao, dataNascimento, ...cleanDiagnosis } = pregnancyDiagnosis as any

    await handleSaveDiagnosis({
      ...cleanDiagnosis,
      diagnostico: diagnosisStatus,
      dataDiagnostico: diagnosisDate,
      previsaoParto: diagnosisStatus === 'prenha' ? previsaoParto : null,
      nascimento: diagnosisStatus === 'prenha' ? savedBirthAnimals : null,
      dataParto: new Date().toISOString(),
      acao: 'Atualizacao'
    })
  }

  const handleSaveAll = async () => {
    if (!pregnancyDiagnosis) return

    const isDateInterval = !!useDateInterval

    // Quando for intervalo, usar a média das datas visuais para dataInicial
    let dataInicialToSave: string
    if (isDateInterval) {
      const dataInicio = watch('coverageProcess.dataInicio') || new Date().toISOString()
      const dataFim = watch('coverageProcess.dataFim') || new Date().toISOString()

      const beginTime = new Date(dataInicio).getTime()
      const endTime = new Date(dataFim).getTime()
      const averageTime = beginTime + (endTime - beginTime) / 2
      dataInicialToSave = new Date(averageTime).toISOString()
    } else {
      dataInicialToSave = watch('pregnancyDiagnosis.dataInicial') || ''
    }

    // Preparar payload do processo de cobertura (apenas se não for intervalo)
    const coveragePayload: CoverageProcessParams | undefined =
      !isDateInterval && coverageProcess
        ? {
            idFazenda: coverageProcess.idFazenda || pregnancyDiagnosis.idFazenda,
            idProcesso: coverageProcess.idProcesso || coverageProcess.id || '',
            idVaca: coverageProcess.idVaca || pregnancyDiagnosis.idAnimal,
            idTouro: pregnancyDiagnosis.touro || null,
            tipoMonta: watch('pregnancyDiagnosis.tipoReproducao') as any,
            dataInicio: dataInicialToSave,
            dataFim: null,
            dataCio: coverageProcess.dataCio || dataInicialToSave,
            idDiagGest: pregnancyDiagnosis.idDiagGest,
            inseminador: pregnancyDiagnosis.inseminador || '',
            dosagem: coverageProcess.dosagem || '',
            cdc: coverageProcess.cdc || '',
            idLote: coverageProcess.idLote || '',
            partida: coverageProcess.partida || null,
            acao: 'Atualizacao'
          }
        : undefined

    // Calcular previsão de parto
    const previsaoParto = calculateBirthForecast(
      diagnosisDate,
      (pregnancyDiagnosis as any)?.tempoGestacao || null,
      pregnancyDiagnosis?.dataCobertura || pregnancyDiagnosis?.dataInicial
    )

    // Preparar payload do diagnóstico
    const diagnosisPayload = {
      idFazenda: pregnancyDiagnosis.idFazenda,
      idAnimal: pregnancyDiagnosis.idAnimal,
      touro: pregnancyDiagnosis.touro,
      idDiagGest: pregnancyDiagnosis.idDiagGest,
      tipoReproducao: pregnancyDiagnosis.tipoReproducao,
      dataInicial: dataInicialToSave,
      dataCobertura: isDateInterval ? null : dataInicialToSave,
      dataDiagnostico: diagnosisDate,
      diagnostico: diagnosisStatus,
      inseminador: pregnancyDiagnosis.inseminador || undefined,
      doses: pregnancyDiagnosis.doses || undefined,
      previsaoParto: diagnosisStatus === 'prenha' ? previsaoParto : null,
      nascimento: diagnosisStatus === 'prenha' ? savedBirthAnimals : null,
      dataParto: new Date().toISOString(),
      acao: 'Atualizacao' as const,
      status: pregnancyDiagnosis.status
    }

    try {
      // Usar o método consolidado se disponível (recomendado)
      if (handleSaveDiagnosisComplete) {
        const result = await handleSaveDiagnosisComplete({
          diagnosis: diagnosisPayload,
          coverageProcess: coveragePayload,
          updateProcess: !!coveragePayload,
          username: username!
          // Só atualiza processo se não for intervalo
        })

        // Atualizar animais salvos com os retornados da API
        if (result?.createdAnimals && result.createdAnimals.length > 0) {
          setSavedBirthAnimals(result.createdAnimals as any)
        }

        // Marcar como salvo para esconder botões
        setHasJustSaved(true)

        toastSyncSuccess('Item atualizado com sucesso!')
      } else {
        // Fallback para o método antigo (sequencial)
        if (coveragePayload) {
          await handleSave(coveragePayload)
        }
        await handleSaveDiagnosisSection()

        // Marcar como salvo para esconder botões
        setHasJustSaved(true)

        toastSyncSuccess("Item atualizad' com sucesso!")
      }

      navigate('/reproducao/lista-de-reproducao')
    } catch (error) {
      toast.error('Não foi possível atualizar os dados, tente novamente.')
    }
  }

  return {
    savedBirthAnimals,
    useDateInterval,
    hasBirthAnimalsFromDiagnosis,
    diagnosisStatus,
    diagnosisDate,
    hasJustSaved,
    handleToggleDateInterval,
    handleSaveMultipleBirths,
    handleRemoveBirth,
    handleSaveAll,
    setError,
    clearErrors
  }
}
